import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import axios from 'axios';

import { getCookie } from '../../../lib/cookies';
import ToeSelector from './ToeSelector';
import GenerateMoModal from './GenerateMoModal';
import {
  deleteMoConversation,
  fetchMoConversationByDbq,
  fetchToeOptions,
  setUserDbqToe,
  searchPagesInFiles,
} from '../../../services/medicalOpinion';
import PageContentModal from './PageContentModal';
import { parseCitations, groupCitationsBySources } from './moUtils';

const DEFAULT_MO_PROMPT = 'Create medical opinion draft.';

// Citation regex for splitting text
const CITATION_REGEX_GLOBAL = /(\(source:\s*[^,]+,\s*pages?\s*\d+(?:-\d+)?(?:,\s*date\s*[\d-]+)?\))/gi;

const formatTranscriptDate = (value) => {
  if (!value) return 'Unknown date';
  try {
    return new Date(value).toLocaleDateString();
  } catch (e) {
    return String(value);
  }
};

const parseMetadata = (metadata) => {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch (e) {
      return null;
    }
  }
  return metadata;
};

const getLatestMetadata = (messages, key) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const meta = parseMetadata(messages[i]?.metadata);
    if (meta && meta[key]) return meta[key];
  }
  return null;
};

/**
 * Renders MO content with clickable citation links
 */
const MoContentRenderer = ({ content, contextFiles, onCitationClick }) => {
  if (!content) return null;

  const citations = parseCitations(content, contextFiles);
  const sources = groupCitationsBySources(citations);

  // Split content by citations and render with clickable links
  const parts = content.split(CITATION_REGEX_GLOBAL);

  const renderContent = () => {
    return parts.map((part, index) => {
      // Check if this part is a citation
      const citation = citations.find((c) => c.text === part);
      if (citation) {
        return (
          <button
            key={index}
            type="button"
            className="dbq-mo-citation-link"
            onClick={() => onCitationClick(citation.fileId, citation.fileName, citation.pageNumber)}
            title={`View ${citation.fileName} page ${citation.pageNumber}`}
          >
            <span className="dbq-mo-citation-icon">📄</span>
            <span className="dbq-mo-citation-text">p.{citation.pageNumber}</span>
          </button>
        );
      }
      // Regular text - render with ReactMarkdown for formatting
      return <ReactMarkdown key={index}>{part}</ReactMarkdown>;
    });
  };

  return (
    <div className="dbq-mo-content-wrapper">
      <div className="dbq-mo-content-body">
        {renderContent()}
      </div>
      {sources.length > 0 && (
        <div className="dbq-mo-sources-section">
          <div className="dbq-mo-sources-title">Sources Referenced</div>
          <div className="dbq-mo-sources-list">
            {sources.map((source, idx) => (
              <div key={idx} className="dbq-mo-source-item">
                <span className="dbq-mo-source-name">{source.fileName}</span>
                <span className="dbq-mo-source-pages">
                  {source.pages.map((p, pIdx) => (
                    <button
                      key={pIdx}
                      type="button"
                      className="dbq-mo-source-page-link"
                      onClick={() => onCitationClick(source.fileId || p.fileId, source.fileName, p.pageNumber)}
                    >
                      p.{p.pageNumber}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MoPanel = ({ userDbq, onApplyToDbq, onCollapse }) => {
  const [toeOptions, setToeOptions] = useState([]);
  const [toeLoading, setToeLoading] = useState(false);
  const [toeSaving, setToeSaving] = useState(false);
  const [selectedToeId, setSelectedToeId] = useState('');

  const [conversationId, setConversationId] = useState(null);
  const [conversationStatus, setConversationStatus] = useState('draft');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(DEFAULT_MO_PROMPT);
  const [isSending, setIsSending] = useState(false);

  const [claimFiles, setClaimFiles] = useState([]);
  const [contextFiles, setContextFiles] = useState([]);
  const [contextTranscripts, setContextTranscripts] = useState([]);
  const [panelTab, setPanelTab] = useState('chat');
  const [investigationStatus, setInvestigationStatus] = useState(null);
  const [investigationLog, setInvestigationLog] = useState(null);
  const [investigationSummary, setInvestigationSummary] = useState(null);
  const [investigationProgress, setInvestigationProgress] = useState(null);
  const [autoInvestigateEnabled, setAutoInvestigateEnabled] = useState(() => {
    // Load from localStorage, default to true
    const stored = localStorage.getItem('mo_auto_investigate');
    return stored === null ? true : stored === 'true';
  });

  // Manual context page additions (enhanced flow)
  const [manualContextPages, setManualContextPages] = useState([]);
  const [selectedFilesToSearch, setSelectedFilesToSearch] = useState([]);
  const [pageSearchQuery, setPageSearchQuery] = useState('');
  const [pageSearchResults, setPageSearchResults] = useState([]);
  const [isSearchingPages, setIsSearchingPages] = useState(false);
  const [showManualSearchForm, setShowManualSearchForm] = useState(false);

  // Generate Medical Opinion modal
  const [showGenerateMoModal, setShowGenerateMoModal] = useState(false);
  const [selectedMessageContent, setSelectedMessageContent] = useState('');

  // Page content modal
  const [showPageContentModal, setShowPageContentModal] = useState(false);
  const [selectedPageInfo, setSelectedPageInfo] = useState({
    fileId: null,
    fileName: null,
    pageNumber: null,
  });

  const messagesEndRef = useRef(null);
  const userEditedInputRef = useRef(false);

  // Persist auto-investigate preference
  useEffect(() => {
    localStorage.setItem('mo_auto_investigate', String(autoInvestigateEnabled));
  }, [autoInvestigateEnabled]);

  const contextFilesToDisplay = contextFiles || [];
  const contextTranscriptsToDisplay = contextTranscripts || [];

  useEffect(() => {
    if (userDbq?.toeId) {
      setSelectedToeId(userDbq.toeId);
    }
  }, [userDbq?.toeId]);

  useEffect(() => {
    let mounted = true;

    const loadToeOptions = async () => {
      setToeLoading(true);
      try {
        const data = await fetchToeOptions();
        if (mounted) setToeOptions(data?.toes || []);
      } catch (e) {
        toast.error('Failed to load TOE options');
      } finally {
        if (mounted) setToeLoading(false);
      }
    };

    loadToeOptions();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!userDbq?.userDbqId) return;
    let mounted = true;

    const loadConversation = async () => {
      try {
        const data = await fetchMoConversationByDbq(userDbq.userDbqId);
        if (!mounted) return;

        const conversation = data?.conversation;
        const loadedMessages = data?.messages || [];
        setConversationId(conversation?.moConversationId || null);
        setConversationStatus(conversation?.status || 'draft');
        setMessages(loadedMessages);

        const latestContext = getLatestMetadata(loadedMessages, 'contextFiles');
        if (latestContext) setContextFiles(latestContext);

        const latestTranscripts = getLatestMetadata(loadedMessages, 'transcripts');
        if (latestTranscripts) setContextTranscripts(latestTranscripts);

      } catch (e) {
        toast.error('Failed to load MO conversation');
      }
    };

    loadConversation();
    return () => {
      mounted = false;
    };
  }, [userDbq?.userDbqId]);

  useEffect(() => {
    if (!userDbq?.claimId) return;
    let mounted = true;

    const loadClaimFiles = async () => {
      try {
        const response = await axios.get(`/claims/${userDbq.claimId}/files`);
        if (!mounted) return;
        const activeFiles = Array.isArray(response.data)
          ? response.data.filter((f) => f.fileActive)
          : [];
        setClaimFiles(activeFiles);
      } catch (error) {
        console.error('Failed to load claim files:', error);
        if (mounted) setClaimFiles([]);
      }
    };

    loadClaimFiles();
    return () => {
      mounted = false;
    };
  }, [userDbq?.claimId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isSending]);

  useEffect(() => {
    if (userEditedInputRef.current) return;
    if (messages.length === 0) {
      setInput(DEFAULT_MO_PROMPT);
    } else if (input === DEFAULT_MO_PROMPT) {
      setInput('');
    }
  }, [messages.length, input]);

  const handleToeChange = async (toeId) => {
    if (!userDbq?.userDbqId) return;
    setToeSaving(true);
    try {
      await setUserDbqToe(userDbq.userDbqId, toeId);
      setSelectedToeId(toeId);
      toast.success('TOE updated for this DBQ');
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Failed to update TOE');
    } finally {
      setToeSaving(false);
    }
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;
    if (!userDbq?.userDbqId) {
      toast.error('DBQ context not ready yet.');
      return;
    }
    if (!selectedToeId) {
      toast.error('Select a Theory of Entitlement before sending.');
      return;
    }

    setIsSending(true);
    setInput('');
    setContextFiles([]);
    setContextTranscripts([]);
    setInvestigationStatus(null);
    setInvestigationLog(null);
    setInvestigationSummary(null);
    setInvestigationProgress(null);

    const userMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMessage, { role: 'assistant', content: '' }]);

    try {
      const token = getCookie('token');
      const url = `${process.env.REACT_APP_API_URL}/casegpt/mo/chat`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-USER-TIMEZONE': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        body: JSON.stringify({
          userDbqId: userDbq.userDbqId,
          message: trimmed,
          moConversationId: conversationId,
          skipInvestigation: !autoInvestigateEnabled,
          manualContextPages: manualContextPages.length > 0 ? manualContextPages : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body.getReader();
      let assistantMessage = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data || data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.contextFiles) {
              setContextFiles(parsed.contextFiles);
            }
            if (parsed.transcripts) {
              setContextTranscripts(parsed.transcripts);
            }
            if (parsed.investigationStatus) {
              setInvestigationStatus(parsed.investigationStatus);
            }
            if (parsed.investigationSummary) {
              setInvestigationSummary(parsed.investigationSummary);
            }
            if (parsed.investigationProgress) {
              setInvestigationProgress(parsed.investigationProgress);
            }
            if (parsed.text !== undefined) {
              assistantMessage += parsed.text;
            }
            if (parsed.isEndOfAiResponseStream) {
              if (parsed.moConversationId) {
                setConversationId(parsed.moConversationId);
              }
              if (parsed.contextFiles) {
                setContextFiles(parsed.contextFiles);
              }
              if (parsed.transcripts) {
                setContextTranscripts(parsed.transcripts);
              }
              if (parsed.investigationLog) {
                setInvestigationLog(parsed.investigationLog);
              }
              if (parsed.investigationSummary) {
                setInvestigationSummary(parsed.investigationSummary);
              }
              setInvestigationStatus(null);
              setInvestigationProgress(null);
            }
          } catch (e) {
            assistantMessage += data;
          }

          const currentMessage = assistantMessage;
          setMessages((prev) => {
            const next = [...prev];
            const lastIndex = next.length - 1;
            if (lastIndex >= 0 && next[lastIndex].role === 'assistant') {
              next[lastIndex] = { ...next[lastIndex], content: currentMessage };
            }
            return next;
          });
        }
      }
    } catch (error) {
      console.error('MO chat error:', error);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        };
        return next;
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleApplyMessage = (messageContent) => {
    setSelectedMessageContent(messageContent);
    setShowGenerateMoModal(true);
  };

  const handleCloseGenerateMoModal = () => {
    setShowGenerateMoModal(false);
    setSelectedMessageContent('');
  };

  const handlePageClick = (fileId, fileName, pageNumber) => {
    setSelectedPageInfo({ fileId, fileName, pageNumber });
    setShowPageContentModal(true);
  };

  const handleClosePageContentModal = () => {
    setShowPageContentModal(false);
    setSelectedPageInfo({ fileId: null, fileName: null, pageNumber: null });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = async () => {
    if (!messages.length && !conversationId) {
      return;
    }
    if (!window.confirm('Clear this MO chat and delete its history?')) {
      return;
    }

    try {
      if (conversationId) {
        await deleteMoConversation(conversationId);
      }
      setConversationId(null);
      setConversationStatus('draft');
      setMessages([]);
      setContextFiles([]);
      setContextTranscripts([]);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Failed to clear chat');
    }
  };

  // Manual context page handlers (enhanced flow)
  const handleToggleFileForSearch = (fileId) => {
    setSelectedFilesToSearch((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSearchPages = async () => {
    if (selectedFilesToSearch.length === 0) {
      toast.error('Please select at least one file to search');
      return;
    }
    if (!pageSearchQuery.trim() || pageSearchQuery.trim().length < 3) {
      toast.error('Please enter a search query (at least 3 characters)');
      return;
    }

    setIsSearchingPages(true);
    setPageSearchResults([]);

    try {
      const result = await searchPagesInFiles(
        userDbq?.claimId,
        selectedFilesToSearch,
        pageSearchQuery.trim()
      );
      setPageSearchResults(result?.pages || []);
      if (result?.pages?.length === 0) {
        toast.info('No matching pages found');
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Failed to search pages');
    } finally {
      setIsSearchingPages(false);
    }
  };

  const handleTogglePageSelection = (page) => {
    const pageKey = `${page.fileId}::${page.pageNumber}`;
    setManualContextPages((prev) => {
      const exists = prev.some(
        (p) => `${p.fileId}::${p.pageNumber}` === pageKey
      );
      if (exists) {
        return prev.filter((p) => `${p.fileId}::${p.pageNumber}` !== pageKey);
      }
      return [...prev, { ...page }];
    });
  };

  const handleRemoveManualPage = (fileId, pageNumber) => {
    setManualContextPages((prev) =>
      prev.filter((p) => !(p.fileId === fileId && p.pageNumber === pageNumber))
    );
  };

  const handleClearManualSearch = () => {
    setSelectedFilesToSearch([]);
    setPageSearchQuery('');
    setPageSearchResults([]);
    setShowManualSearchForm(false);
  };

  const isPageSelected = (page) => {
    return manualContextPages.some(
      (p) => p.fileId === page.fileId && p.pageNumber === page.pageNumber
    );
  };

  const handleRegenerate = () => {
    if (manualContextPages.length === 0) {
      toast.error('Please add at least one page to context before regenerating');
      return;
    }
    setInput('Regenerate the Medical Opinion using the additional context I have provided.');
    // Trigger send after state update
    setTimeout(() => {
      const sendBtn = document.querySelector('.dbq-mo-send-btn');
      if (sendBtn) sendBtn.click();
    }, 100);
  };

  const isToeMissing = !selectedToeId;

  return (
    <div className="dbq-mo-panel-inner">
      <div className="dbq-mo-header">
        <div>
          <h4>Medical Opinion Assistant</h4>
        </div>
        <div className="dbq-mo-header-actions">
          <div className="dbq-mo-tabs dbq-mo-panel-tabs">
            <button
              type="button"
              className={`dbq-mo-tab ${panelTab === 'chat' ? 'active' : ''}`}
              onClick={() => setPanelTab('chat')}
            >
              Chat
            </button>
            <button
              type="button"
              className={`dbq-mo-tab ${panelTab === 'context' ? 'active' : ''}`}
              onClick={() => setPanelTab('context')}
            >
              Context
            </button>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClearChat}
            disabled={isSending}
          >
            Clear Chat
          </Button>
          {onCollapse && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onCollapse}
            >
              Collapse
            </Button>
          )}
        </div>
      </div>

      {panelTab === 'chat' && (
        <>
          <div className="dbq-mo-section dbq-mo-toe">
            <div className="dbq-mo-toe-row">
              <span className="dbq-mo-toe-label">TOE</span>
              {toeLoading ? (
                <div className="text-muted small">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </div>
              ) : (
                <ToeSelector
                  value={selectedToeId}
                  options={toeOptions}
                  onChange={handleToeChange}
                  disabled={toeSaving}
                />
              )}
            </div>
            <div className="dbq-mo-investigate-toggle">
              <Form.Check
                type="switch"
                id="auto-investigate-toggle"
                label="Auto-investigate"
                checked={autoInvestigateEnabled}
                onChange={(e) => setAutoInvestigateEnabled(e.target.checked)}
                disabled={isSending}
              />
              <span className="dbq-mo-toggle-hint">
                {autoInvestigateEnabled
                  ? 'AI will search for answers to its own questions'
                  : 'AI will ask clarifying questions directly'}
              </span>
            </div>
            {isToeMissing && (
              <Alert variant="warning" className="mt-2 py-2">
                Select a TOE before using the MO assistant.
              </Alert>
            )}
          </div>

          <div className="dbq-mo-chat">
            <div className="dbq-mo-messages">
              {messages.map((msg, idx) => (
                <div key={`${msg.role}-${idx}`} className={`dbq-mo-message ${msg.role}`}>
                  <div className="dbq-mo-message-bubble">
                    {msg.role === 'assistant' ? (
                      <MoContentRenderer
                        content={msg.content}
                        contextFiles={contextFilesToDisplay}
                        onCitationClick={handlePageClick}
                      />
                    ) : (
                      msg.content
                    )}
                  </div>
                  {msg.role === 'assistant' && msg.content?.trim() && (
                    <div className="dbq-mo-message-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleApplyMessage(msg.content)}
                        disabled={isSending}
                      >
                        Generate Medical Opinion
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {(investigationStatus || investigationProgress) && (
                <div className="dbq-mo-investigation-live">
                  <div className="dbq-mo-investigation-header">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="dbq-mo-investigation-title">
                      {investigationProgress?.type === 'complete'
                        ? 'Investigation complete'
                        : 'Auto-investigating...'}
                    </span>
                    {investigationProgress?.iteration && investigationProgress?.maxIterations && (
                      <span className="dbq-mo-investigation-iteration-badge">
                        Round {investigationProgress.iteration}/{investigationProgress.maxIterations}
                      </span>
                    )}
                  </div>
                  {investigationProgress?.message && (
                    <div className="dbq-mo-investigation-message">
                      {investigationProgress.message}
                    </div>
                  )}
                  {investigationProgress?.type === 'investigating_question' && (
                    <div className="dbq-mo-investigation-progress-bar">
                      <div
                        className="dbq-mo-investigation-progress-fill"
                        style={{
                          width: `${((investigationProgress.questionIndex || 0) / (investigationProgress.totalQuestions || 1)) * 100}%`,
                        }}
                      />
                    </div>
                  )}
                  {investigationProgress?.type === 'question_result' && (
                    <div className={`dbq-mo-investigation-result-inline ${investigationProgress.evidenceFound > 0 ? 'found' : 'not-found'}`}>
                      {investigationProgress.evidenceFound > 0 ? (
                        <>
                          <span className="dbq-mo-check">✓</span>
                          {investigationProgress.evidenceFound} match{investigationProgress.evidenceFound !== 1 ? 'es' : ''}
                          {investigationProgress.topMatch && (
                            <span className="dbq-mo-match-preview"> — {investigationProgress.topMatch}</span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="dbq-mo-x">✗</span>
                          No matches
                        </>
                      )}
                    </div>
                  )}
                  {investigationProgress?.type === 'questions_extracted' && investigationProgress.questions?.length > 0 && (
                    <div className="dbq-mo-questions-preview">
                      {investigationProgress.questions.slice(0, 3).map((q, idx) => (
                        <div key={idx} className="dbq-mo-question-preview-item">
                          <span className="dbq-mo-question-bullet">•</span>
                          {q.length > 80 ? `${q.slice(0, 80)}...` : q}
                        </div>
                      ))}
                      {investigationProgress.questions.length > 3 && (
                        <div className="dbq-mo-question-more">
                          +{investigationProgress.questions.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="dbq-mo-input-row">
              <Form.Control
                type="text"
                placeholder="Type a message..."
                value={input}
                onChange={(e) => {
                  userEditedInputRef.current = true;
                  setInput(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                disabled={isSending || isToeMissing}
              />
              <Button
                className="dbq-mo-send-btn"
                variant="primary"
                onClick={handleSend}
                disabled={isSending || isToeMissing || !input.trim()}
              >
                {isSending ? 'Sending...' : 'Send'}
              </Button>
              {messages.length > 0 && (
                <Button
                  variant="outline-secondary"
                  onClick={handleRegenerate}
                  disabled={isSending || isToeMissing}
                  title={manualContextPages.length > 0 
                    ? `Regenerate with ${manualContextPages.length} manual page(s)` 
                    : 'Add pages in Context tab first'}
                >
                  Regenerate
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {panelTab === 'context' && (
        <div className="dbq-mo-context-panel">
          <div className="dbq-mo-context-header">
            <div>
              <div className="small text-muted">Context</div>
              <div className="small">
                {contextFilesToDisplay.length} files / {claimFiles.length} total ·{' '}
                {contextTranscriptsToDisplay.length} transcript
                {contextTranscriptsToDisplay.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <div className="dbq-mo-context-scroll">
            <div className="dbq-mo-context-block">
              <div className="dbq-mo-context-title">Files</div>
              {contextFilesToDisplay.length === 0 ? (
                <div className="text-muted small">
                  {isSending
                    ? 'Selecting context files based on the TOE and DBQ...'
                    : 'Context files are automatically selected after you send a message.'}
                </div>
              ) : (
                <ul className="dbq-mo-context-list">
                  {contextFilesToDisplay.map((file) => (
                    <li key={file.fileId || file.fileName} className="dbq-mo-context-file">
                      <div className="dbq-mo-context-file-header">
                        <span className="dbq-mo-context-file-name">{file.fileName}</span>
                        {file.selectionSource && (
                          <span className="text-muted ms-2">
                            ({file.selectionSource}
                            {typeof file.selectionScore === 'number'
                              ? `: ${file.selectionScore.toFixed(2)}`
                              : ''}
                            )
                          </span>
                        )}
                        {!file.included && (
                          <span className="text-muted ms-2">(not loaded)</span>
                        )}
                      </div>
                      {file.topPages && file.topPages.length > 0 && (
                        <div className="dbq-mo-context-pages">
                          <span className="text-muted small">
                            Top pages:{' '}
                            {file.topPages.map((p, idx) => (
                              <span key={p.pageNumber}>
                                {idx > 0 && ', '}
                                <button
                                  type="button"
                                  className="dbq-mo-page-link"
                                  onClick={() => handlePageClick(file.fileId, file.fileName, p.pageNumber)}
                                  title={`View page ${p.pageNumber} content`}
                                >
                                  p.{p.pageNumber}
                                </button>
                                <span className="dbq-mo-page-score">({p.score})</span>
                              </span>
                            ))}
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="dbq-mo-context-block dbq-mo-manual-pages">
              <div className="dbq-mo-context-title-row">
                <div className="dbq-mo-context-title">
                  Manual Additions
                  {manualContextPages.length > 0 && (
                    <span className="dbq-mo-manual-count">
                      {manualContextPages.length} page{manualContextPages.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {!showManualSearchForm && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowManualSearchForm(true)}
                    disabled={claimFiles.length === 0}
                  >
                    + Search Pages
                  </Button>
                )}
              </div>

              {/* Selected pages display */}
              {manualContextPages.length > 0 && (
                <div className="dbq-mo-selected-pages">
                  {manualContextPages.map((page) => (
                    <div key={`${page.fileId}::${page.pageNumber}`} className="dbq-mo-selected-page">
                      <button
                        type="button"
                        className="dbq-mo-page-link"
                        onClick={() => handlePageClick(page.fileId, page.fileName, page.pageNumber)}
                      >
                        {page.fileName} - p.{page.pageNumber}
                      </button>
                      <span className="dbq-mo-page-score">({page.score?.toFixed(2)})</span>
                      <button
                        type="button"
                        className="dbq-mo-remove-page"
                        onClick={() => handleRemoveManualPage(page.fileId, page.pageNumber)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showManualSearchForm && (
                <div className="dbq-mo-page-search-form">
                  {/* Step 1: Select files to search */}
                  <div className="dbq-mo-search-step">
                    <div className="dbq-mo-search-step-label">1. Select files to search</div>
                    <div className="dbq-mo-file-checkboxes">
                      {claimFiles.map((file) => (
                        <Form.Check
                          key={file.fileId}
                          type="checkbox"
                          id={`file-search-${file.fileId}`}
                          label={file.fileName}
                          checked={selectedFilesToSearch.includes(file.fileId)}
                          onChange={() => handleToggleFileForSearch(file.fileId)}
                          className="dbq-mo-file-checkbox"
                        />
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Enter search query */}
                  <div className="dbq-mo-search-step">
                    <div className="dbq-mo-search-step-label">2. Enter search query</div>
                    <div className="dbq-mo-search-input-row">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="e.g., 'diagnosis date for low back pain'"
                        value={pageSearchQuery}
                        onChange={(e) => setPageSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchPages()}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSearchPages}
                        disabled={isSearchingPages || selectedFilesToSearch.length === 0 || pageSearchQuery.length < 3}
                      >
                        {isSearchingPages ? <Spinner animation="border" size="sm" /> : 'Search'}
                      </Button>
                    </div>
                  </div>

                  {/* Step 3: Search results */}
                  {pageSearchResults.length > 0 && (
                    <div className="dbq-mo-search-step">
                      <div className="dbq-mo-search-step-label">3. Select pages to include</div>
                      <div className="dbq-mo-search-results">
                        {pageSearchResults.map((page) => {
                          const selected = isPageSelected(page);
                          return (
                            <div
                              key={`${page.fileId}::${page.pageNumber}`}
                              className={`dbq-mo-search-result ${selected ? 'selected' : ''}`}
                              onClick={() => handleTogglePageSelection(page)}
                            >
                              <div className="dbq-mo-result-header">
                                <Form.Check
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => {}}
                                  className="dbq-mo-result-checkbox"
                                />
                                <span className="dbq-mo-result-file">{page.fileName}</span>
                                <span className="dbq-mo-result-page">p.{page.pageNumber}</span>
                                <span className="dbq-mo-result-score">{page.score?.toFixed(2)}</span>
                                <button
                                  type="button"
                                  className="dbq-mo-result-view"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePageClick(page.fileId, page.fileName, page.pageNumber);
                                  }}
                                >
                                  View
                                </button>
                              </div>
                              {page.textPreview && (
                                <div className="dbq-mo-result-preview">
                                  {page.textPreview}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="dbq-mo-search-actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleClearManualSearch}
                    >
                      Close
                    </Button>
                    {manualContextPages.length > 0 && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => {
                          handleClearManualSearch();
                          handleRegenerate();
                        }}
                      >
                        Regenerate with {manualContextPages.length} page{manualContextPages.length !== 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {manualContextPages.length === 0 && !showManualSearchForm && (
                <div className="text-muted small">
                  Search through files to find and add specific pages the AI may have missed.
                </div>
              )}
            </div>

            <div className="dbq-mo-context-block">
              <div className="dbq-mo-context-title">Transcripts</div>
              {contextTranscriptsToDisplay.length === 0 ? (
                <div className="text-muted small">No transcripts available.</div>
              ) : (
                <ul className="dbq-mo-context-list dbq-mo-transcripts-list">
                  {contextTranscriptsToDisplay.map((transcript) => (
                    <li key={transcript.transcriptId}>
                      <div className="dbq-mo-transcript-title">
                        {formatTranscriptDate(transcript.meetingDatetime)}
                      </div>
                      {transcript.preview && (
                        <div className="dbq-mo-transcript-preview">
                          {transcript.preview}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {investigationSummary && (
              <div className="dbq-mo-context-block">
                <div className="dbq-mo-context-title">Auto-Investigation Summary</div>
                <div className="dbq-mo-investigation-summary">
                  <div className="dbq-mo-investigation-stats-grid">
                    <div className="dbq-mo-stat">
                      <span className="dbq-mo-stat-value">{investigationSummary.totalIterations || 0}</span>
                      <span className="dbq-mo-stat-label">Iterations</span>
                    </div>
                    <div className="dbq-mo-stat">
                      <span className="dbq-mo-stat-value">{investigationSummary.totalQuestionsInvestigated || 0}</span>
                      <span className="dbq-mo-stat-label">Questions</span>
                    </div>
                    <div className="dbq-mo-stat">
                      <span className="dbq-mo-stat-value">{investigationSummary.totalEvidenceFound || 0}</span>
                      <span className="dbq-mo-stat-label">Evidence Found</span>
                    </div>
                    <div className="dbq-mo-stat">
                      <span className="dbq-mo-stat-value">{investigationSummary.questionsAnswered || 0}</span>
                      <span className="dbq-mo-stat-label">Answered</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {investigationLog && investigationLog.length > 0 && (
              <div className="dbq-mo-context-block">
                <div className="dbq-mo-context-title">Investigation Log</div>
                <ul className="dbq-mo-investigation-log">
                  {investigationLog
                    .filter((entry) => entry.question)
                    .map((entry, idx) => (
                      <li key={idx} className="dbq-mo-investigation-entry">
                        <div className="dbq-mo-investigation-question">
                          <span className="dbq-mo-investigation-iteration">
                            [{entry.iteration}]
                          </span>
                          {entry.question}
                        </div>
                        <div className="dbq-mo-investigation-result">
                          {entry.evidenceFound > 0 ? (
                            <span className="dbq-mo-evidence-found">
                              ✓ {entry.evidenceFound} match{entry.evidenceFound !== 1 ? 'es' : ''}
                              {entry.topMatch && (
                                <span className="dbq-mo-top-match"> — {entry.topMatch}</span>
                              )}
                            </span>
                          ) : (
                            <span className="dbq-mo-no-evidence">No matches found</span>
                          )}
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Context files are auto-selected server-side */}

      {/* Generate Medical Opinion Modal */}
      <GenerateMoModal
        show={showGenerateMoModal}
        onHide={handleCloseGenerateMoModal}
        initialText={selectedMessageContent}
        userDbq={userDbq}
        onApplyToDbq={onApplyToDbq}
      />

      {/* Page Content Modal */}
      <PageContentModal
        show={showPageContentModal}
        onHide={handleClosePageContentModal}
        claimId={userDbq?.claimId}
        fileId={selectedPageInfo.fileId}
        fileName={selectedPageInfo.fileName}
        pageNumber={selectedPageInfo.pageNumber}
      />
    </div>
  );
};

export default MoPanel;

